import { baseApi } from "@/shared/api";
import { decrementPendingActionsCount } from "@/features/auth";
import type {
  VendorResponse,
  CreateVendorDto,
  UpdateVendorDto,
  InventoryItemResponse,
  InventoryItemListItem,
  InventoryUnit,
  AssetAssignmentResponse,
  AssignUnitResponse,
  InventoryMovementResponse,
  AssignItemDto,
  ReturnItemDto,
  WriteOffItemDto,
  ReturnUnitResponse,
  WriteOffUnitResponse,
  DepreciationForecast,
  PublicUnitInfo,
  PaginatedResponse,
  ItemRequest,
  ItemRequestListItem,
  CreateItemRequestDto,
  RejectRequestDto,
  FulfillRequestDto,
  FulfillResponse,
  MarkUnavailableDto,
  TransferUnitDto,
  TransferResponse,
  BulkTransferDto,
  BulkTransferResponse,
  RevalueUnitDto,
  RevalueResponse,
  BatchAssignRequest,
  BatchAssignResponse,
  BatchWriteOffRequest,
  BatchWriteOffResponse,
  WarehouseUnitsParams,
  WarehouseBatchesParams,
  UnitsSummaryResponse,
} from "../types";
import type { MovementType } from "../types/domain";

export const warehouseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getVendors: builder.query<PaginatedResponse<VendorResponse>, void>({
      query: () => ({
        url: `/api/warehouse/vendors/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: [{ type: "InventoryItems" as const, id: "VENDORS_LIST" }],
    }),

    getVendor: builder.query<VendorResponse, number>({
      query: (id) => ({
        url: `/api/warehouse/vendors/${id}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "InventoryItems" as const, id: `VENDOR_${id}` }],
    }),

    createVendor: builder.mutation<VendorResponse, CreateVendorDto>({
      query: (body) => ({
        url: `/api/warehouse/vendors/`,
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "InventoryItems" as const, id: "VENDORS_LIST" }],
    }),

    updateVendor: builder.mutation<VendorResponse, { id: number; body: UpdateVendorDto }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/vendors/${id}/`,
        method: "PATCH",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "InventoryItems" as const, id: `VENDOR_${id}` },
        { type: "InventoryItems" as const, id: "VENDORS_LIST" },
      ],
    }),

    deleteVendor: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/warehouse/vendors/${id}/`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "InventoryItems" as const, id: `VENDOR_${id}` },
        { type: "InventoryItems" as const, id: "VENDORS_LIST" },
      ],
    }),

    getWarehouseUnits: builder.query<InventoryUnit[], WarehouseUnitsParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.asset_type) searchParams.append("asset_type", params.asset_type);
        if (params?.asset_type__in) searchParams.append("asset_type__in", params.asset_type__in);
        if (params?.status) searchParams.append("status", params.status);
        if (params?.inventory_batch) searchParams.append("inventory_batch", String(params.inventory_batch));
        if (params?.assigned_to) searchParams.append("assigned_to", String(params.assigned_to));
        if (params?.search) searchParams.append("search", params.search);
        if (params?.ordering) searchParams.append("ordering", params.ordering);
        const queryString = searchParams.toString();
        return {
          url: queryString ? `/api/warehouse/units/?${queryString}` : `/api/warehouse/units/`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: [{ type: "InventoryUnits" as const, id: "LIST" }],
    }),

    getWarehouseUnitDetail: builder.query<InventoryUnit, number>({
      query: (id) => ({
        url: `/api/warehouse/units/${id}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "InventoryUnits" as const, id }],
    }),

    getUnitForecast: builder.query<DepreciationForecast, { id: number; months?: number }>({
      query: ({ id, months = 12 }) => ({
        url: `/api/warehouse/units/${id}/forecast/`,
        method: "GET",
        params: {
          months,
        },
        credentials: "include",
      }),
      providesTags: (_result, _error, { id }) => [{ type: "InventoryUnits" as const, id }],
    }),

    assignUnit: builder.mutation<AssignUnitResponse, { id: number; body: AssignItemDto }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/units/${id}/assign/`,
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id, body }) => [
        { type: "InventoryUnits" as const, id },
        { type: "InventoryUnits" as const, id: "LIST" },
        { type: "InventoryItems" as const, id: "ASSIGNMENTS_LIST" },
        { type: "InventoryItems" as const, id: "MOVEMENTS_LIST" },
        { type: "ItemRequests" as const, id: "LIST" },
        { type: "ItemRequests" as const, id: "APPROVED_LIST" },
        ...(body.unit_request_id != null
          ? [{ type: "ItemRequests" as const, id: body.unit_request_id }]
          : []),
      ],
    }),

    returnUnit: builder.mutation<ReturnUnitResponse, { id: number; body?: ReturnItemDto }>({
      query: ({ id, body = {} }) => ({
        url: `/api/warehouse/units/${id}/return/`,
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "InventoryUnits" as const, id },
        { type: "InventoryUnits" as const, id: "LIST" },
        { type: "InventoryItems" as const, id: "ASSIGNMENTS_LIST" },
      ],
    }),

    writeOffUnit: builder.mutation<WriteOffUnitResponse, { id: number; body: WriteOffItemDto }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/units/${id}/write-off/`,
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "InventoryUnits" as const, id },
        { type: "InventoryUnits" as const, id: "LIST" },
        { type: "InventoryItems" as const, id: "MOVEMENTS_LIST" },
      ],
    }),

    getWarehouseItems: builder.query<InventoryItemListItem[], WarehouseBatchesParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.asset_type) searchParams.append("asset_type", params.asset_type);
        if (params?.asset_type__in) searchParams.append("asset_type__in", params.asset_type__in);
        const queryString = searchParams.toString();
        return {
          url: queryString ? `/api/warehouse/batches/?${queryString}` : `/api/warehouse/batches/`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: [{ type: "InventoryItems" as const, id: "LIST" }],
    }),

    getWarehouseUnitsSummary: builder.query<UnitsSummaryResponse, WarehouseUnitsParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.asset_type) searchParams.append("asset_type", params.asset_type);
        if (params?.asset_type__in) searchParams.append("asset_type__in", params.asset_type__in);
        if (params?.status) searchParams.append("status", params.status);
        if (params?.search) searchParams.append("search", params.search);
        if (params?.inventory_batch) searchParams.append("inventory_batch", String(params.inventory_batch));
        if (params?.assigned_to) searchParams.append("assigned_to", String(params.assigned_to));
        const queryString = searchParams.toString();
        return {
          url: queryString ? `/api/warehouse/units/summary/?${queryString}` : `/api/warehouse/units/summary/`,
          method: "GET",
          credentials: "include" as const,
        };
      },
      providesTags: [{ type: "InventoryUnits" as const, id: "LIST" }],
    }),

    getWarehouseItemDetail: builder.query<InventoryItemResponse, number>({
      query: (id) => ({
        url: `/api/warehouse/batches/${id}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "InventoryItems" as const, id }],
    }),

    getAssignments: builder.query<AssetAssignmentResponse[], void>({
      query: () => ({
        url: `/api/warehouse/assignments/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: [{ type: "InventoryItems" as const, id: "ASSIGNMENTS_LIST" }],
    }),

    getAssignment: builder.query<AssetAssignmentResponse, number>({
      query: (id) => ({
        url: `/api/warehouse/assignments/${id}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "InventoryItems" as const, id: `ASSIGNMENT_${id}` }],
    }),

    returnAssignment: builder.mutation<InventoryItemResponse, { id: number; body?: { notes?: string } }>({
      query: ({ id, body = {} }) => ({
        url: `/api/warehouse/assignments/${id}/return/`,
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "InventoryItems" as const, id: `ASSIGNMENT_${id}` },
        { type: "InventoryItems" as const, id: "ASSIGNMENTS_LIST" },
        { type: "InventoryItems" as const, id: "LIST" },
        { type: "InventoryItems" as const, id: "MOVEMENTS_LIST" },
        { type: "InventoryUnits" as const, id: "LIST" },
      ],
    }),

    getMovements: builder.query<InventoryMovementResponse[], { item?: number; type?: MovementType } | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.item) searchParams.append("item", String(params.item));
        if (params?.type) searchParams.append("type", params.type);
        const queryString = searchParams.toString();
        return {
          url: queryString ? `/api/warehouse/movements/?${queryString}` : `/api/warehouse/movements/`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: [{ type: "InventoryItems" as const, id: "MOVEMENTS_LIST" }],
    }),

    getMovement: builder.query<InventoryMovementResponse, number>({
      query: (id) => ({
        url: `/api/warehouse/movements/${id}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "InventoryItems" as const, id: `MOVEMENT_${id}` }],
    }),

    getPublicUnitInfo: builder.query<PublicUnitInfo, string>({
      query: (barcode) => ({
        url: `/api/warehouse/public/units/${barcode}/`,
        method: "GET",
        credentials: "omit",
      }),
    }),

    getItemRequests: builder.query<ItemRequestListItem[], void>({
      query: () => ({
        url: `/api/warehouse/requests/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "ItemRequests" as const, id: "LIST" }],
    }),

    getApprovedItemRequests: builder.query<ItemRequestListItem[], void>({
      query: () => ({
        url: `/api/warehouse/requests/?status=approved`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "ItemRequests" as const, id: "APPROVED_LIST" }],
    }),

    getItemRequest: builder.query<ItemRequest, number>({
      query: (id) => ({
        url: `/api/warehouse/requests/${id}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, id) => [{ type: "ItemRequests" as const, id }],
    }),

    createItemRequest: builder.mutation<ItemRequest, CreateItemRequestDto>({
      query: (body) => ({
        url: `/api/warehouse/requests/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [
        { type: "ItemRequests" as const, id: "LIST" },
        "PendingActions",
        { type: "SelfService" as const, id: "REQUESTS" },
      ],
    }),

    approveItemRequest: builder.mutation<ItemRequest, number>({
      query: (id) => ({
        url: `/api/warehouse/requests/${id}/approve/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ItemRequests" as const, id: "LIST" },
        { type: "ItemRequests" as const, id },
        "PendingActions",
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),

    rejectItemRequest: builder.mutation<ItemRequest, { id: number; body: RejectRequestDto }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/requests/${id}/reject/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ItemRequests" as const, id: "LIST" },
        { type: "ItemRequests" as const, id },
        "PendingActions",
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),

    cancelItemRequest: builder.mutation<ItemRequest, number>({
      query: (id) => ({
        url: `/api/warehouse/requests/${id}/cancel/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ItemRequests" as const, id: "LIST" },
        { type: "ItemRequests" as const, id },
        { type: "SelfService" as const, id: "REQUESTS" },
      ],
    }),

    fulfillItemRequest: builder.mutation<FulfillResponse, { id: number; body: FulfillRequestDto }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/requests/${id}/fulfill/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ItemRequests" as const, id: "LIST" },
        { type: "ItemRequests" as const, id },
        { type: "InventoryUnits" as const, id: "LIST" },
        "PendingActions",
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),

    markUnavailableItemRequest: builder.mutation<ItemRequest, { id: number; body: MarkUnavailableDto }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/requests/${id}/mark_unavailable/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ItemRequests" as const, id: "LIST" },
        { type: "ItemRequests" as const, id },
        "PendingActions",
        { type: "SelfService" as const, id: "REQUESTS" },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(decrementPendingActionsCount());
      },
    }),

    transferUnit: builder.mutation<TransferResponse, { id: number; body: TransferUnitDto }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/units/${id}/transfer/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "InventoryUnits" as const, id },
        { type: "InventoryUnits" as const, id: "LIST" },
      ],
    }),

    bulkTransferUnits: builder.mutation<BulkTransferResponse, BulkTransferDto>({
      query: (body) => ({
        url: `/api/warehouse/units/bulk-transfer/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "InventoryUnits" as const, id: "LIST" }],
    }),

    revalueUnit: builder.mutation<RevalueResponse, { id: number; body: RevalueUnitDto }>({
      query: ({ id, body }) => ({
        url: `/api/warehouse/units/${id}/revalue/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "InventoryUnits" as const, id },
        { type: "InventoryUnits" as const, id: "LIST" },
      ],
    }),

    batchAssignUnits: builder.mutation<BatchAssignResponse, { batchId: number; body: BatchAssignRequest }>({
      query: ({ batchId, body }) => ({
        url: `/api/warehouse/batches/${batchId}/assign/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [
        { type: "InventoryUnits" as const, id: "LIST" },
        { type: "InventoryItems" as const, id: "LIST" },
        { type: "ItemRequests" as const, id: "LIST" },
        { type: "ItemRequests" as const, id: "APPROVED_LIST" },
      ],
    }),

    batchWriteOffUnits: builder.mutation<BatchWriteOffResponse, { batchId: number; body: BatchWriteOffRequest }>({
      query: ({ batchId, body }) => ({
        url: `/api/warehouse/batches/${batchId}/write-off/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: [
        { type: "InventoryUnits" as const, id: "LIST" },
        { type: "InventoryItems" as const, id: "LIST" },
        { type: "InventoryItems" as const, id: "MOVEMENTS_LIST" },
      ],
    }),
  }),
});

export const {
  useGetVendorsQuery,
  useGetVendorQuery,
  useLazyGetVendorQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useDeleteVendorMutation,
  useGetWarehouseUnitsQuery,
  useGetWarehouseUnitDetailQuery,
  useLazyGetWarehouseUnitDetailQuery,
  useGetUnitForecastQuery,
  useGetWarehouseUnitsSummaryQuery,
  useAssignUnitMutation,
  useReturnUnitMutation,
  useWriteOffUnitMutation,
  useGetWarehouseItemsQuery,
  useGetWarehouseItemDetailQuery,
  useLazyGetWarehouseItemDetailQuery,
  useGetAssignmentsQuery,
  useGetAssignmentQuery,
  useLazyGetAssignmentQuery,
  useReturnAssignmentMutation,
  useGetMovementsQuery,
  useGetMovementQuery,
  useLazyGetMovementQuery,
  useGetPublicUnitInfoQuery,
  useGetItemRequestsQuery,
  useGetApprovedItemRequestsQuery,
  useGetItemRequestQuery,
  useCreateItemRequestMutation,
  useApproveItemRequestMutation,
  useRejectItemRequestMutation,
  useCancelItemRequestMutation,
  useFulfillItemRequestMutation,
  useMarkUnavailableItemRequestMutation,
  useTransferUnitMutation,
  useBulkTransferUnitsMutation,
  useRevalueUnitMutation,
  useBatchAssignUnitsMutation,
  useBatchWriteOffUnitsMutation,
} = warehouseApi;

